import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { CotizacionDTO, CotizacionStatus, PagaebleResult, SearchCotizacionDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    dueView,
    editQuoteLocation,
    errorMessage,
    NEW_QUOTE_PATH,
    pageWindow,
    QUOTE_TEMPLATES_PATH,
    QUOTES_PATH,
    quoteStatusView,
    trackingView,
} from '../common/quote-common'
import type { DueView, QuoteLocationState, QuoteStatusChip, TrackingView } from '../common/quote-common'

export const PAGE_SIZES = [20, 50, 100]
const TEXT_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de cotizaciones.'

export interface QuoteListFilters {
    noCotizacion: string
    nombre: string
    status: CotizacionStatus | ''
    ramo: string
}

export type QuoteTextFilter = 'noCotizacion' | 'nombre'

export interface QuoteRow {
    id: number
    noCotizacion: number | null
    ramo: string | null
    name: string
    tipo: 'Cliente' | 'Prospecto'
    vencimiento: DueView
    tracking: TrackingView
    status: QuoteStatusChip
}

const defaultFilters: QuoteListFilters = {
    noCotizacion: '',
    nombre: '',
    status: '',
    ramo: '',
}

export const NEW_QUOTE_LOCATION: { pathname: string; state: QuoteLocationState } = {
    pathname: NEW_QUOTE_PATH,
    state: { backUrl: QUOTES_PATH },
}

export const TEMPLATES_LOCATION: { pathname: string; state: QuoteLocationState } = {
    pathname: QUOTE_TEMPLATES_PATH,
    state: { backUrl: QUOTES_PATH },
}

const buildQuery = (filters: QuoteListFilters) => {
    const params: string[] = ['order=fecVencimiento%20asc']
    const noCotizacion = filters.noCotizacion.replace(/\D/g, '')
    if (noCotizacion) params.push(`noCotizacion[eq]=${noCotizacion}`)
    if (filters.nombre.trim()) params.push(`nombre[cn]=${encodeURIComponent(filters.nombre.trim())}`)
    if (filters.status) params.push(`status[eq]=${filters.status}`)
    if (filters.ramo) params.push(`ramo=${encodeURIComponent(filters.ramo)}`)
    return params.join('&')
}

const toRow = (item: SearchCotizacionDTO): QuoteRow => ({
    id: item.id,
    noCotizacion: item.noCotizacion,
    ramo: item.ramo,
    name: (item.cliente || item.prospecto || '').trim(),
    tipo: item.clienteId ? 'Cliente' : 'Prospecto',
    vencimiento: dueView(item.fecVencimiento, item.status),
    tracking: trackingView(item.fechaVista, item.fechaEnvio),
    status: quoteStatusView(item.status),
})

export const useQuoteListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const filtersRef = useRef<QuoteListFilters>(defaultFilters)
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [filters, setFilters] = useState<QuoteListFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters))
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<SearchCotizacionDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(() => (location.state as QuoteLocationState | null)?.detailId || null)

    useEffect(() => () => clearTimeout(textTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/cotizaciones?page=${page}&size=${pageSize}&${query}`)
                const payload: WrapperResponse<PagaebleResult<SearchCotizacionDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, query, reloadToken])

    const commit = (next: QuoteListFilters) => {
        clearTimeout(textTimer.current)
        filtersRef.current = next
        setFilters(next)
        setPage(1)
        setQuery(buildQuery(next))
    }

    const commitPatch = (partial: Partial<QuoteListFilters>) => commit({ ...filtersRef.current, ...partial })

    const changeText = (key: QuoteTextFilter, value: string) => {
        const next = { ...filtersRef.current, [key]: key === 'noCotizacion' ? value.replace(/\D/g, '') : value }
        filtersRef.current = next
        setFilters(next)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => {
            setPage(1)
            setQuery(buildQuery(filtersRef.current))
        }, TEXT_DELAY_MS)
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const deleted = (cotizacion?: CotizacionDTO | null) => {
        const id = cotizacion?.id ?? detailId
        setItems((current) => current.filter((item) => item.id !== id))
        setTotalItems((current) => Math.max(0, current - 1))
        setDetailId(null)
    }

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        filters,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        detailId,
        changeText,
        submitText: () => commit(filtersRef.current),
        changeStatus: (value: string) => commitPatch({ status: value as CotizacionStatus | '' }),
        changeRamo: (ramo: string) => commitPatch({ ramo }),
        clearFilters: () => commit({ ...defaultFilters }),
        setPage,
        changePageSize,
        reload: () => setReloadToken((token) => token + 1),
        openCotizacion: (id: number) => setDetailId(id),
        closeCotizacion: () => setDetailId(null),
        editCotizacion: (id: number) => history.push(editQuoteLocation(id)),
        changed: () => setReloadToken((token) => token + 1),
        deleted,
    }
}
